import { router, usePage } from '@inertiajs/react';
import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import type { HackerWithdrawalsShow } from '@/types';

const SHELL_CLASS =
  'theme-ph26 selection-on-light flex min-h-[100dvh] items-center justify-center bg-background px-6 font-mono text-primary';

// /hacker/:publicId/withdraw — withdraw-confirmation.tsx. The action's success was local
// state over there; here the PRG re-renders the page with `alreadyWithdrawn` set, which
// is the same "Cedimos tu cupo" screen.
export default function Show({
  publicId,
  eventName,
  alreadyWithdrawn,
  canWithdraw,
}: HackerWithdrawalsShow) {
  const errors = usePage().props.errors as unknown as Record<
    string,
    string | undefined
  >;
  const [isPending, setIsPending] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const error = errors.base ?? null;

  const handleConfirm = () => {
    setIsPending(true);
    router.post(
      `/hacker/${publicId}/withdraw`,
      {},
      {
        onSuccess: () => setDialogOpen(false),
        onFinish: () => setIsPending(false),
      },
    );
  };

  if (alreadyWithdrawn) {
    return (
      <div className={SHELL_CLASS}>
        <div className="w-full max-w-md space-y-6 text-center">
          <h1 className="font-mono font-semibold text-3xl uppercase tracking-tight">
            Cedimos tu cupo
          </h1>
          <p className="text-primary/70 text-sm">
            Liberamos tu lugar en {eventName}. Esperamos verte en futuras
            ediciones de Platanus Hack.
          </p>
        </div>
      </div>
    );
  }

  if (!canWithdraw) {
    return (
      <div className={SHELL_CLASS}>
        <div className="w-full max-w-md space-y-6 text-center">
          <h1 className="font-mono font-semibold text-3xl uppercase tracking-tight">
            No tienes un cupo activo
          </h1>
          <p className="text-primary/70 text-sm">
            No encontramos un cupo confirmado en {eventName} que puedas ceder.
            Si crees que es un error, escríbenos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={SHELL_CLASS}>
      <div className="w-full max-w-md space-y-8">
        <div className="space-y-3">
          <h1 className="font-mono font-semibold text-3xl uppercase tracking-tight">
            ¿Cederás tu cupo en {eventName}?
          </h1>
          <p className="text-primary/70 text-sm leading-relaxed">
            Si confirmas, liberaremos tu lugar para que otro hacker pueda
            asistir. Esta acción no se puede revertir.
          </p>
        </div>

        {error && (
          <p className="border border-red-500/40 bg-red-500/10 px-3 py-2 text-red-400 text-sm">
            {error}
          </p>
        )}

        <Button
          variant="destructive"
          size="lg"
          className="w-full"
          onClick={() => setDialogOpen(true)}
        >
          Ceder mi cupo
        </Button>
      </div>

      <AlertDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!isPending) setDialogOpen(open);
        }}
      >
        <AlertDialogContent className="theme-ph26 selection-on-light bg-background font-mono text-primary">
          <AlertDialogHeader>
            <AlertDialogTitle className="uppercase">
              ¿Confirmas que cedes tu cupo?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-primary/70">
              Liberaremos tu lugar en {eventName}. Esta acción no se puede
              revertir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={isPending}
            >
              {isPending ? 'Cediendo cupo…' : 'Sí, cedo mi cupo'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
